import * as Tone from 'tone';
import { bootstrapTelegram, getWebApp, haptic, notify, isIOS } from './telegram.js';
import { AudioEngine, type Track } from './audio-engine.js';
import {
  fetchSketch,
  fetchStem,
  updateTrackGain,
  updateTrackOffset,
  updateTrackPan,
  updateTrackStartAt,
  uploadTrack,
} from './api-client.js';
import { TapTempo } from './metronome.js';
import { icon, type IconName } from './icons.js';

const els = {
  tracksList: document.getElementById('tracks-list') as HTMLUListElement,
  record: document.getElementById('record') as HTMLButtonElement,
  recordIcon: document.getElementById('record-icon')!,
  addTrack: document.getElementById('add-track') as HTMLButtonElement,
  addTrackIcon: document.getElementById('add-track-icon')!,
  settingsToggle: document.getElementById('settings-toggle') as HTMLButtonElement,
  settingsToggleIcon: document.getElementById('settings-toggle-icon')!,
  settingsDrawer: document.getElementById('settings-drawer') as HTMLElement,
  mixerRows: document.getElementById('mixer-rows') as HTMLElement,
  send: document.getElementById('send') as HTMLButtonElement,
  tempoDisplay: document.getElementById('tempo-display')!,
  countInToggle: document.getElementById('count-in-toggle') as HTMLButtonElement,
  tapTempo: document.getElementById('tap-tempo') as HTMLButtonElement,
  speakerBoostToggle: document.getElementById('speaker-boost-toggle') as HTMLButtonElement,
  metronomeRecToggle: document.getElementById('metronome-rec-toggle') as HTMLButtonElement,
  offsetValue: document.getElementById('offset-value')!,
  offsetUp: document.getElementById('offset-up') as HTMLButtonElement,
  offsetDown: document.getElementById('offset-down') as HTMLButtonElement,
  play: document.getElementById('play') as HTMLButtonElement,
  playIcon: document.getElementById('play-icon')!,
  rewind: document.getElementById('rewind') as HTMLButtonElement,
  rewindIcon: document.getElementById('rewind-icon')!,
  transportTime: document.getElementById('transport-time')!,
  timelineArea: document.getElementById('timeline-area')!,
  timelineRuler: document.getElementById('timeline-ruler')!,
  timelinePlayhead: document.getElementById('timeline-playhead')!,
};

const LATENCY_STORAGE_KEY = 'skizzen.latencyOffsetMs';
// Default 0 — the latency-compensation path sets offsetMs = -latency on
// fresh recordings, which translates to AudioBufferSourceNode.start(t,
// offset) with a non-zero offset arg. iOS Safari has a long-standing
// bug where that arg causes pitch artifacts. Until we fix the playback
// path (slice the buffer instead of relying on the offset arg), keep
// the default at 0 so users don't hit the bug by accident.
const DEFAULT_LATENCY_MS = 0;
const COUNT_IN_STORAGE_KEY = 'skizzen.countInEnabled';
const SPEAKER_BOOST_STORAGE_KEY = 'skizzen.speakerBoostOn';
const SPEAKER_BOOST_AMOUNT = 4;
const METRONOME_REC_STORAGE_KEY = 'skizzen.metronomeDuringRec';
const MIXER_STORAGE_KEY = 'skizzen.mixerMode';
const LATENCY_STEP_MS = 10;

const engine = new AudioEngine();
const tapTempo = new TapTempo();
let timer: ReturnType<typeof setInterval> | null = null;
let isUploading = false;
let isLoading = false;

// Sketch context — null = brand-new sketch will be created on first upload
let sketchId: string | null = null;
let sketchName: string | null = null;

function fmtMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}

// setStatus / setHint used to write into a #status section and a #hint
// paragraph. Both elements were removed in the transport-centric redesign;
// keep the calls as no-ops so the existing wiring throughout main.ts
// doesn't need touch-up. The states they conveyed (loading, recording,
// counting in) are now expressed visually via the transport buttons.
function setStatus(_: string): void { /* no-op — status row removed */ }
function setHint(_: string): void { /* no-op — hint row removed */ }

// Show a runtime error in the existing red error banner (above the app).
// Auto-clears after a few seconds unless `sticky` is true. Replaces the
// silent setStatus/setHint flow for errors that the user actually needs
// to see (mic permission denied, audio init failure, etc.).
let errorClearTimer: ReturnType<typeof setTimeout> | null = null;
function showError(msg: string, sticky = false): void {
  const banner = document.getElementById('load-error');
  if (!banner) return;
  banner.textContent = msg;
  banner.hidden = false;
  if (errorClearTimer) clearTimeout(errorClearTimer);
  if (!sticky) {
    errorClearTimer = setTimeout(() => {
      banner.hidden = true;
      errorClearTimer = null;
    }, 6000);
  }
}

function refreshAddTrackButton(): void {
  els.addTrack.disabled =
    isLoading || isUploading || engine.isRecording || engine.isCountingIn;
}

function refreshTrackList(): void {
  closeSettingsPanel();
  els.tracksList.innerHTML = '';
  for (const t of engine.tracks) {
    els.tracksList.appendChild(renderTrack(t));
  }
  renderRuler(engine.sketchDurationMs, els.timelineRuler);
  refreshTransportTime();
  updatePlayheadPosition();
  refreshSendButton();
  refreshPlayButton();
  refreshRecordButton();
  refreshAddTrackButton();
  if (!els.settingsDrawer.hidden) renderMixerRows();
}

function refreshTempo(): void {
  els.tempoDisplay.textContent = `${engine.metronome.getBpm()} BPM`;
  if (engine.getCountInEnabled()) {
    els.countInToggle.classList.add('active');
  } else {
    els.countInToggle.classList.remove('active');
  }
}

function refreshOffset(): void {
  els.offsetValue.textContent = `${engine.getLatencyOffsetMs()} ms`;
}

function setLatencyOffset(ms: number): void {
  engine.setLatencyOffsetMs(ms);
  try {
    localStorage.setItem(LATENCY_STORAGE_KEY, String(engine.getLatencyOffsetMs()));
  } catch {
    /* private mode / quota etc. — ignore */
  }
  refreshOffset();
}

function loadStoredOffset(): void {
  let latency = DEFAULT_LATENCY_MS;
  try {
    const stored = localStorage.getItem(LATENCY_STORAGE_KEY);
    if (stored !== null) {
      const n = parseInt(stored, 10);
      if (!Number.isNaN(n)) latency = n;
    }
  } catch {
    /* ignore */
  }
  engine.setLatencyOffsetMs(latency);
}

function setCountInEnabled(on: boolean): void {
  engine.setCountInEnabled(on);
  try {
    localStorage.setItem(COUNT_IN_STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
  refreshTempo();
}

function loadStoredCountIn(): void {
  try {
    const stored = localStorage.getItem(COUNT_IN_STORAGE_KEY);
    if (stored === '1') engine.setCountInEnabled(true);
  } catch {
    /* ignore */
  }
}

function refreshSpeakerBoost(): void {
  const on = engine.getOverdubBoost() > 1;
  if (on) els.speakerBoostToggle.classList.add('active');
  else els.speakerBoostToggle.classList.remove('active');
}

function setSpeakerBoostEnabled(on: boolean): void {
  engine.setOverdubBoost(on ? SPEAKER_BOOST_AMOUNT : 1);
  try {
    localStorage.setItem(SPEAKER_BOOST_STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
  refreshSpeakerBoost();
}

function loadStoredSpeakerBoost(): void {
  // Default ON for iOS (where the routing-to-earpiece problem actually
  // exists), OFF elsewhere. Persisted preference wins over the default.
  let on = isIOS();
  try {
    const stored = localStorage.getItem(SPEAKER_BOOST_STORAGE_KEY);
    if (stored !== null) on = stored === '1';
  } catch {
    /* ignore */
  }
  engine.setOverdubBoost(on ? SPEAKER_BOOST_AMOUNT : 1);
}

function refreshMetronomeRec(): void {
  if (engine.getMetronomeDuringRecording()) {
    els.metronomeRecToggle.classList.add('active');
  } else {
    els.metronomeRecToggle.classList.remove('active');
  }
}

function setMetronomeDuringRecording(on: boolean): void {
  engine.setMetronomeDuringRecording(on);
  try {
    localStorage.setItem(METRONOME_REC_STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
  refreshMetronomeRec();
}

function loadStoredMetronomeRec(): void {
  try {
    const stored = localStorage.getItem(METRONOME_REC_STORAGE_KEY);
    if (stored === '1') engine.setMetronomeDuringRecording(true);
  } catch {
    /* ignore */
  }
}

function isDrawerOpen(): boolean {
  return !els.settingsDrawer.hidden;
}

function setSettingsDrawerOpen(open: boolean): void {
  els.settingsDrawer.hidden = !open;
  els.settingsToggle.classList.toggle('active', open);
  els.settingsToggle.setAttribute('aria-expanded', String(open));
  document.body.dataset.mixer = open ? 'true' : 'false';
  if (open) renderMixerRows();
  try {
    localStorage.setItem(MIXER_STORAGE_KEY, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function loadStoredDrawerState(): void {
  let open = false;
  try {
    open = localStorage.getItem(MIXER_STORAGE_KEY) === '1';
  } catch {
    /* ignore */
  }
  setSettingsDrawerOpen(open);
}

function refreshSendButton(): void {
  // Empty tracks (placeholder slots from "+") don't count — nothing to send.
  const audible = engine.tracks.filter(
    (t) => !t.muted && !(t.durationMs === 0 && typeof t.recordingMs !== 'number'),
  );
  const enabled =
    audible.length > 0 && !engine.isRecording && !isUploading && !isLoading;
  els.send.disabled = !enabled;
  if (isUploading) els.send.textContent = 'sending…';
  else if (isLoading) els.send.textContent = 'loading…';
  else if (audible.length === 0) els.send.textContent = 'send sketch';
  else els.send.textContent = `send sketch (${audible.length} track${audible.length === 1 ? '' : 's'})`;
}

function refreshPlayButton(): void {
  const audible = engine.tracks.filter(
    (t) => !t.muted && !(t.durationMs === 0 && typeof t.recordingMs !== 'number'),
  );
  const enabled = audible.length > 0 && !engine.isRecording && !engine.isCountingIn && !isLoading;
  els.play.disabled = !enabled;
  if (engine.isPlaying) {
    els.play.classList.add('active');
    setButtonIcon(els.playIcon, 'pause', 16);
    els.play.setAttribute('aria-label', 'Pause');
  } else {
    els.play.classList.remove('active');
    setButtonIcon(els.playIcon, 'play', 16);
    els.play.setAttribute('aria-label', 'Play sketch');
  }
}

function refreshRecordButton(): void {
  const disabled = isLoading || isUploading || engine.isPlaying;
  els.record.disabled = disabled;
  const isLive = engine.isRecording || engine.isCountingIn;
  document.body.dataset.recording = isLive ? 'true' : 'false';
  if (engine.isCountingIn) {
    els.record.classList.add('active', 'counting-in');
    els.record.classList.remove('recording');
    setButtonIcon(els.recordIcon, 'circle', 16);
    els.record.setAttribute('aria-label', 'Stop count-in');
  } else if (engine.isRecording) {
    els.record.classList.add('active', 'recording');
    els.record.classList.remove('counting-in');
    setButtonIcon(els.recordIcon, 'stop', 16);
    els.record.setAttribute('aria-label', 'Stop recording');
  } else {
    els.record.classList.remove('active', 'recording', 'counting-in');
    setButtonIcon(els.recordIcon, 'circle', 18);
    els.record.setAttribute('aria-label', 'Record');
  }
}

function renderWaveform(peaks: number[]): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const width = 100; // viewBox units; scales via CSS
  const height = 28;
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('class', 'track-wave');

  const cy = height / 2;
  const barWidth = width / peaks.length;
  for (let i = 0; i < peaks.length; i++) {
    // Floor a tiny minimum so silent buckets still show a flat line
    const barH = Math.max(1.5, peaks[i] * (height - 2));
    const rect = document.createElementNS(ns, 'rect');
    const x = i * barWidth + barWidth * 0.15;
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(cy - barH / 2));
    rect.setAttribute('width', String(barWidth * 0.7));
    rect.setAttribute('height', String(barH));
    rect.setAttribute('rx', '0.6');
    svg.appendChild(rect);
  }
  return svg;
}

/** Pixels per millisecond used for visually shifting track waveforms. */
const OFFSET_PX_PER_MS = 0.2;
/** Step size when nudging a track's offset via the +/- buttons. */
const OFFSET_STEP_MS = 10;

function fmtOffset(ms: number): string {
  if (ms === 0) return '0 ms';
  const sign = ms > 0 ? '+' : '−';
  return `${sign}${Math.abs(ms)} ms`;
}

function setButtonIcon(btn: HTMLElement, name: IconName, size = 16): void {
  btn.replaceChildren(icon(name, size));
}

function formatMmSs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatMmSsCs(ms: number): string {
  const totalCs = Math.max(0, Math.floor(ms / 100));
  const cs = totalCs % 10;
  const totalSec = Math.floor(totalCs / 10);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}.${cs}`;
}

function renderRuler(durationMs: number, ruler: HTMLElement): void {
  ruler.innerHTML = '';
  const durationSec = durationMs / 1000;
  const tickEverySec = durationSec <= 30 ? 1 : 5;
  const labelEverySec = durationSec <= 30 ? 5 : 10;
  for (let s = 0; s <= durationSec + 0.001; s += tickEverySec) {
    const ratio = (s * 1000) / durationMs;
    if (ratio > 1.001) break;
    const tick = document.createElement('span');
    tick.className = 'ruler-tick';
    tick.style.left = `${(ratio * 100).toFixed(3)}%`;
    if (Math.abs(s % labelEverySec) < 0.001) {
      tick.textContent = formatMmSs(s * 1000);
      tick.classList.add('labeled');
    }
    ruler.appendChild(tick);
  }
}

/**
 * Paint a track's waveform into a canvas, positioned per startAtMs and clipped
 * to the lane's full timeline width. Width and left are set as inline styles.
 *
 * If the track is the live recording target (`recordingMs` is set), peaks are
 * sourced from `engine.getRecordingPeaks` and the clip extends to the running
 * duration. Otherwise peaks are computed from the decoded buffer.
 */
function paintLaneCanvas(canvas: HTMLCanvasElement, t: Track, areaWidthPx: number): void {
  const sketchDurationMs = engine.sketchDurationMs;
  const isLiveRecording = typeof t.recordingMs === 'number';
  const trackDurMs = isLiveRecording ? (t.recordingMs ?? 0) : t.buffer.duration * 1000;
  // Effective position = recorded position + user offset (the unified
  // control). Negative effective position means the clip starts before
  // the timeline; the lane's overflow:hidden naturally clips the part
  // that would otherwise paint left of zero.
  const effectiveStartMs = t.startAtMs + t.offsetMs;
  const widthRatio = trackDurMs / sketchDurationMs;
  const leftRatio = effectiveStartMs / sketchDurationMs; // can be negative

  const widthPx = Math.max(1, Math.round(widthRatio * areaWidthPx));
  const heightPx = 32;
  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));

  canvas.style.left = `${(leftRatio * 100).toFixed(3)}%`;
  canvas.style.width = `${(widthRatio * 100).toFixed(3)}%`;
  canvas.style.height = `${heightPx}px`;
  canvas.width = widthPx * dpr;
  canvas.height = heightPx * dpr;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = t.muted
    ? 'rgba(127,127,127,0.45)'
    : isLiveRecording
      ? 'rgba(215,58,73,0.85)'
      : 'rgba(64,120,200,0.75)';

  const buckets = widthPx;
  let peaks: number[];
  if (isLiveRecording) {
    const live = engine.getRecordingPeaks(t.id, buckets);
    peaks = live ? live.peaks : new Array(buckets * 2).fill(0);
  } else {
    peaks = computePeaksAt(t.buffer, buckets);
  }
  const mid = heightPx / 2;
  // Visually scale peak heights by the track's gain so the waveform
  // grows / shrinks with the level fader. Clamped to ±1 so peaks past
  // unity gain stay inside the lane height instead of clipping out.
  const gainScale = t.gain;
  for (let i = 0; i < buckets; i++) {
    const min = Math.max(-1, Math.min(1, peaks[i * 2] * gainScale));
    const max = Math.max(-1, Math.min(1, peaks[i * 2 + 1] * gainScale));
    const top = mid - max * mid;
    const bot = mid - min * mid;
    ctx.fillRect(i, top, 1, Math.max(1, bot - top));
  }
}

/** Returns interleaved [min0,max0,min1,max1,...] of length 2*bucketCount. */
function computePeaksAt(buffer: AudioBuffer, bucketCount: number): number[] {
  const data = buffer.getChannelData(0);
  const samplesPerBucket = Math.max(1, Math.ceil(data.length / bucketCount));
  const out = new Array<number>(bucketCount * 2);
  for (let b = 0; b < bucketCount; b++) {
    const start = b * samplesPerBucket;
    const end = Math.min(start + samplesPerBucket, data.length);
    let min = 1;
    let max = -1;
    for (let i = start; i < end; i++) {
      const v = data[i];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (end <= start) { min = 0; max = 0; }
    out[b * 2] = min;
    out[b * 2 + 1] = max;
  }
  return out;
}

function refreshTransportTime(): void {
  const live = engine.getLivePlayheadMs();
  const total = engine.sketchDurationMs;
  els.transportTime.textContent = `${formatMmSsCs(live)} / ${formatMmSs(total)}`;
}

function updatePlayheadPosition(): void {
  const live = engine.getLivePlayheadMs();
  const total = engine.sketchDurationMs;
  const ratio = Math.min(1, Math.max(0, live / total));
  const widthPx = els.timelineArea.clientWidth;
  els.timelinePlayhead.style.transform = `translateX(${(ratio * widthPx).toFixed(1)}px)`;
}

let playheadRaf: number | null = null;
function startPlayheadLoop(): void {
  if (playheadRaf != null) return;
  const tick = () => {
    updatePlayheadPosition();
    refreshTransportTime();
    playheadRaf = requestAnimationFrame(tick);
  };
  playheadRaf = requestAnimationFrame(tick);
}
function stopPlayheadLoop(): void {
  if (playheadRaf != null) {
    cancelAnimationFrame(playheadRaf);
    playheadRaf = null;
  }
}

// Live waveform painter during recording. Throttled to ~10 Hz (every 100 ms)
// so a long take doesn't burn CPU on mobile. Each tick: pull live peaks from
// the engine for the recording-target track, update its lane's recordingMs,
// and repaint that lane's canvas.
let liveRecordTimer: ReturnType<typeof setInterval> | null = null;
function startLiveRecordPaint(): void {
  if (liveRecordTimer != null) return;
  const tick = () => {
    const tid = (engine as unknown as { _recordingTrackId: string | null })._recordingTrackId;
    if (!tid) return;
    const t = engine.tracks.find((x) => x.id === tid);
    if (!t) return;
    const areaWidth = els.timelineArea.clientWidth || 320;
    const live = engine.getRecordingPeaks(t.id, Math.max(32, areaWidth));
    if (live) {
      t.recordingMs = live.durationMs;
    }
    // Ruler may need to grow as the sketch duration grows
    renderRuler(engine.sketchDurationMs, els.timelineRuler);
    // Repaint every lane (cheap; only the recording lane's canvas truly changes)
    for (const x of engine.tracks) {
      const li = els.tracksList.querySelector(`li.track-lane[data-track-id="${x.id}"]`) as HTMLElement | null;
      if (!li) continue;
      const canvas = li.querySelector('canvas.lane-canvas') as HTMLCanvasElement | null;
      if (!canvas) continue;
      paintLaneCanvas(canvas, x, areaWidth);
    }
    // Refresh the recording lane's duration label
    const recLi = els.tracksList.querySelector(`li.track-lane[data-track-id="${tid}"]`) as HTMLElement | null;
    if (recLi) {
      const durEl = recLi.querySelector('.lane-duration');
      if (durEl) durEl.textContent = fmtMs(Math.round(t.recordingMs ?? 0));
    }
  };
  liveRecordTimer = setInterval(tick, 100);
}
function stopLiveRecordPaint(): void {
  if (liveRecordTimer != null) {
    clearInterval(liveRecordTimer);
    liveRecordTimer = null;
  }
}

function renderTrack(t: Track): HTMLLIElement {
  const li = document.createElement('li');
  const classes = ['track-lane'];
  if (t.muted) classes.push('muted');
  if (typeof t.recordingMs === 'number') classes.push('recording');
  li.className = classes.join(' ');
  li.dataset.trackId = t.id;
  li.dataset.armed = t.armed ? 'true' : 'false';
  const isEmpty = t.durationMs === 0 && typeof t.recordingMs !== 'number';
  li.dataset.empty = isEmpty ? 'true' : 'false';

  const header = document.createElement('div');
  header.className = 'lane-header';

  const name = document.createElement('span');
  name.className = 'lane-name';
  name.textContent = t.name;

  const dur = document.createElement('span');
  dur.className = 'lane-duration';
  // Hide the timestamp on empty tracks — "0:00" is just visual noise
  // before any audio has been recorded onto them.
  dur.textContent = isEmpty ? '' : fmtMs(t.durationMs);

  const btns = document.createElement('div');
  btns.className = 'lane-btns';

  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.className = 'lane-btn';
  setButtonIcon(muteBtn, t.muted ? 'volume-x' : 'volume-2', 14);
  muteBtn.title = t.muted ? 'unmute' : 'mute';
  muteBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    engine.setMuted(t.id, !t.muted);
    haptic('light');
    refreshTrackList();
  });

  // Per-track arm/record button: toggles whether this track is the
  // record target. Only one track armed at a time (single-arm invariant
  // is enforced by engine.armTrack). When armed, the button turns red.
  const armBtn = document.createElement('button');
  armBtn.type = 'button';
  const armClasses = ['lane-record-btn'];
  if (t.armed) armClasses.push('armed');
  if (t.armed && (engine.isRecording || engine.isCountingIn)) armClasses.push('recording');
  armBtn.className = armClasses.join(' ');
  setButtonIcon(armBtn, 'circle', 14);
  armBtn.title = t.armed
    ? 'armed — global record records onto this track'
    : 'arm for recording';
  armBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (engine.isRecording || engine.isCountingIn) return;
    engine.armTrack(t.armed ? null : t.id);
    haptic('light');
    refreshTrackList();
  });

  const delBtn = document.createElement('button');
  delBtn.type = 'button';
  delBtn.className = 'lane-btn danger';
  setButtonIcon(delBtn, 'trash-2', 14);
  delBtn.title = 'delete';
  delBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    engine.removeTrack(t.id);
    haptic('medium');
    refreshTrackList();
  });

  btns.append(muteBtn, armBtn, delBtn);
  header.append(name, dur, btns);

  const wave = document.createElement('div');
  wave.className = 'lane-waveform';
  const canvas = document.createElement('canvas');
  canvas.className = 'lane-canvas';
  wave.appendChild(canvas);

  // Mixer controls (level/pan/offset) live in the settings drawer, not
  // inline. Lane is just header + waveform now.
  li.append(header, wave);

  // Defer painting until layout is settled so we can read .clientWidth.
  // requestAnimationFrame is enough because li is already appended by the
  // caller (refreshTrackList) before the next paint frame.
  requestAnimationFrame(() => {
    const areaWidth = els.timelineArea.clientWidth || 320;
    paintLaneCanvas(canvas, t, areaWidth);
  });

  return li;
}

function fmtPercent(gain: number): string {
  return `${Math.round(gain * 100)}%`;
}

function fmtPan(pan: number): string {
  const v = Math.round(pan * 100);
  if (v === 0) return 'C';
  return v < 0 ? `L${Math.abs(v)}` : `R${v}`;
}

interface MixerControlOpts {
  label: string;
  initial: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onInput: (v: number) => void;
  onCommit: () => void;
}

/**
 * Build one column of the lane-mixer row: label + current value (top),
 * horizontal slider (bottom). Used for level, pan, and offset. The
 * label name doubles as a class on the column so CSS can size each
 * column differently (level is widest, pan/offset narrower).
 */
function buildMixerControl(opts: MixerControlOpts): HTMLDivElement {
  const ctl = document.createElement('div');
  ctl.className = `lane-mixer-control ${opts.label}`;

  const labelRow = document.createElement('div');
  labelRow.className = 'lane-mixer-label-row';

  const labelEl = document.createElement('span');
  labelEl.className = 'lane-mixer-label';
  labelEl.textContent = opts.label;

  const valueEl = document.createElement('span');
  valueEl.className = 'lane-mixer-value';
  valueEl.textContent = opts.format(opts.initial);

  labelRow.append(labelEl, valueEl);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'lane-mixer-fader';
  slider.min = String(opts.min);
  slider.max = String(opts.max);
  slider.step = String(opts.step);
  slider.value = String(opts.initial);
  slider.setAttribute('aria-label', opts.label);
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    valueEl.textContent = opts.format(v);
    opts.onInput(v);
  });
  slider.addEventListener('change', () => {
    opts.onCommit();
  });

  ctl.append(labelRow, slider);
  return ctl;
}

/**
 * Populate the settings drawer with one mixer row per non-empty track.
 * Empty tracks (placeholder slots from "+") are skipped — there's
 * nothing to mix until they have audio.
 */
function renderMixerRows(): void {
  els.mixerRows.innerHTML = '';
  for (const t of engine.tracks) {
    if (t.durationMs === 0 && typeof t.recordingMs !== 'number') continue;
    const row = document.createElement('div');
    row.className = 'mixer-row';
    row.dataset.trackId = t.id;

    const label = document.createElement('span');
    label.className = 'mixer-row-label';
    label.textContent = t.name;
    row.appendChild(label);

    row.append(
      buildMixerControl({
        label: 'level',
        initial: Math.round(t.gain * 100),
        min: 0,
        max: 200,
        step: 1,
        format: (v) => fmtPercent(v / 100),
        onInput: (v) => {
          t.gain = v / 100;
          engine.setTrackGain(t.id, t.gain);
          repaintLaneCanvas(t);
        },
        onCommit: () => void syncTrackGainToServer(t),
      }),
      buildMixerControl({
        label: 'pan',
        initial: Math.round(t.pan * 100),
        min: -100,
        max: 100,
        step: 1,
        format: (v) => fmtPan(v / 100),
        onInput: (v) => {
          t.pan = v / 100;
          engine.setTrackPan(t.id, t.pan);
        },
        onCommit: () => void syncTrackPanToServer(t),
      }),
      buildMixerControl({
        label: 'offset',
        initial: Math.round(t.offsetMs),
        min: -250,
        max: 250,
        step: 1,
        format: (v) => fmtOffset(v),
        onInput: (v) => {
          engine.setTrackOffsetMs(t.id, v);
          repaintLaneCanvas(t);
        },
        onCommit: () => void syncTrackOffsetToServer(t),
      }),
    );

    els.mixerRows.appendChild(row);
  }
}

let openPanel: HTMLDivElement | null = null;
let openPanelTrackId: string | null = null;
let panelOutsideHandler: ((ev: MouseEvent | TouchEvent) => void) | null = null;

function closeSettingsPanel(): void {
  if (!openPanel) return;
  const panel = openPanel;
  openPanel = null;
  openPanelTrackId = null;
  if (panelOutsideHandler) {
    document.removeEventListener('pointerdown', panelOutsideHandler, true);
    panelOutsideHandler = null;
  }
  panel.classList.remove('open');
  const remove = () => panel.remove();
  panel.addEventListener('transitionend', remove, { once: true });
  setTimeout(remove, 220);
}

function openSettingsPanel(row: HTMLLIElement, t: Track): void {
  if (openPanelTrackId === t.id) {
    closeSettingsPanel();
    return;
  }
  if (openPanel) closeSettingsPanel();

  const panel = document.createElement('div');
  panel.className = 'track-settings-panel';
  panel.setAttribute('role', 'dialog');
  panel.addEventListener('pointerdown', (ev) => ev.stopPropagation());

  const header = document.createElement('div');
  header.className = 'track-settings-header';
  const title = document.createElement('span');
  title.className = 'track-settings-title';
  title.textContent = t.name;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'track-settings-close';
  closeBtn.setAttribute('aria-label', 'close');
  setButtonIcon(closeBtn, 'x', 14);
  closeBtn.addEventListener('click', () => {
    haptic('light');
    closeSettingsPanel();
  });
  header.append(title, closeBtn);

  // "start" and "offset" used to be separate controls. Now unified into
  // a single "offset" — it can be negative (moves the clip left of its
  // recorded position) and ranges wide enough for full repositioning.
  const levelRow = buildLevelRow(t);
  const panRow = buildPanRow(t);
  const offsetRow = buildOffsetRow(t);

  panel.append(header, levelRow, panRow, offsetRow);
  // Mount on body (not inside the lane) so the lane's overflow:hidden doesn't
  // clip the drawer's height. The fixed positioning anchors it to the viewport.
  document.body.appendChild(panel);

  openPanel = panel;
  openPanelTrackId = t.id;

  // Force a reflow so the transition runs on class add
  void panel.getBoundingClientRect();
  panel.classList.add('open');

  panelOutsideHandler = (ev) => {
    const target = ev.target as Node | null;
    if (target && panel.contains(target)) return;
    closeSettingsPanel();
  };
  document.addEventListener('pointerdown', panelOutsideHandler, true);
}

function buildLevelRow(t: Track): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'track-settings-row';

  const label = document.createElement('span');
  label.className = 'track-settings-label';
  label.textContent = 'level';

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '200';
  slider.step = '1';
  slider.value = String(Math.round(t.gain * 100));
  slider.className = 'track-settings-slider';

  const value = document.createElement('span');
  value.className = 'track-settings-value';
  value.textContent = fmtPercent(t.gain);

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'track-settings-reset';
  reset.textContent = 'reset';

  const apply = (raw: number) => {
    const gain = raw / 100;
    engine.setTrackGain(t.id, gain);
    value.textContent = fmtPercent(t.gain);
    slider.value = String(Math.round(t.gain * 100));
  };

  slider.addEventListener('input', () => apply(parseInt(slider.value, 10)));
  slider.addEventListener('change', () => {
    void syncTrackGainToServer(t);
  });
  reset.addEventListener('click', () => {
    apply(100);
    haptic('light');
    void syncTrackGainToServer(t);
  });

  row.append(label, slider, value, reset);
  return row;
}

function buildPanRow(t: Track): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'track-settings-row';

  const label = document.createElement('span');
  label.className = 'track-settings-label';
  label.textContent = 'pan';

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '-100';
  slider.max = '100';
  slider.step = '1';
  slider.value = String(Math.round(t.pan * 100));
  slider.className = 'track-settings-slider';

  const value = document.createElement('span');
  value.className = 'track-settings-value';
  value.textContent = fmtPan(t.pan);

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'track-settings-reset';
  reset.textContent = 'reset';

  const apply = (raw: number) => {
    const pan = raw / 100;
    engine.setTrackPan(t.id, pan);
    value.textContent = fmtPan(t.pan);
    slider.value = String(Math.round(t.pan * 100));
  };

  slider.addEventListener('input', () => apply(parseInt(slider.value, 10)));
  slider.addEventListener('change', () => {
    void syncTrackPanToServer(t);
  });
  reset.addEventListener('click', () => {
    apply(0);
    haptic('light');
    void syncTrackPanToServer(t);
  });

  row.append(label, slider, value, reset);
  return row;
}

function buildOffsetRow(t: Track): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'track-settings-row';

  const label = document.createElement('span');
  label.className = 'track-settings-label';
  label.textContent = 'offset';

  const down = document.createElement('button');
  down.type = 'button';
  down.className = 'track-offset-btn';
  down.textContent = '−';

  const value = document.createElement('span');
  value.className = 'track-settings-value track-offset-value';
  value.textContent = fmtOffset(t.offsetMs);

  const up = document.createElement('button');
  up.type = 'button';
  up.className = 'track-offset-btn';
  up.textContent = '+';

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'track-settings-reset';
  reset.textContent = 'reset';

  const apply = (next: number) => {
    engine.setTrackOffsetMs(t.id, next);
    value.textContent = fmtOffset(t.offsetMs);
    repaintLaneCanvas(t);
    void syncTrackOffsetToServer(t);
  };

  down.addEventListener('click', () => {
    haptic('light');
    apply(t.offsetMs - OFFSET_STEP_MS);
  });
  up.addEventListener('click', () => {
    haptic('light');
    apply(t.offsetMs + OFFSET_STEP_MS);
  });
  reset.addEventListener('click', () => {
    haptic('light');
    apply(0);
  });

  const controls = document.createElement('span');
  controls.className = 'track-offset-controls';
  controls.append(down, value, up);

  row.append(label, controls, reset);
  return row;
}

const START_STEP_MS = 100;

function buildStartRow(t: Track): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'track-settings-row';

  const label = document.createElement('span');
  label.className = 'track-settings-label';
  label.textContent = 'start';

  const down = document.createElement('button');
  down.type = 'button';
  down.className = 'track-offset-btn';
  down.textContent = '−';

  const value = document.createElement('span');
  value.className = 'track-settings-value track-offset-value';
  value.textContent = formatMmSsCs(t.startAtMs);

  const up = document.createElement('button');
  up.type = 'button';
  up.className = 'track-offset-btn';
  up.textContent = '+';

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'track-settings-reset';
  reset.textContent = 'reset';

  const apply = (next: number) => {
    engine.setTrackStartAtMs(t.id, next);
    value.textContent = formatMmSsCs(t.startAtMs);
    repaintLaneCanvas(t);
    renderRuler(engine.sketchDurationMs, els.timelineRuler);
    void syncTrackStartAtToServer(t);
  };

  down.addEventListener('click', () => {
    haptic('light');
    apply(t.startAtMs - START_STEP_MS);
  });
  up.addEventListener('click', () => {
    haptic('light');
    apply(t.startAtMs + START_STEP_MS);
  });
  reset.addEventListener('click', () => {
    haptic('light');
    apply(0);
  });

  const controls = document.createElement('span');
  controls.className = 'track-offset-controls';
  controls.append(down, value, up);

  row.append(label, controls, reset);
  return row;
}

function repaintLaneCanvas(t: Track): void {
  const li = els.tracksList.querySelector(`li.track-lane[data-track-id="${t.id}"]`) as HTMLElement | null;
  if (!li) return;
  const canvas = li.querySelector('canvas.lane-canvas') as HTMLCanvasElement | null;
  if (!canvas) return;
  paintLaneCanvas(canvas, t, els.timelineArea.clientWidth || 320);
}

/**
 * Debounced per-track persistence — adjusting the offset rapidly via +/−
 * shouldn't spam the server. We coalesce within 400ms per track id.
 */
const offsetSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();
function syncTrackOffsetToServer(t: Track): void {
  if (!sketchId) return;
  if (!t.id || /^t_/.test(t.id)) return;
  const existing = offsetSyncTimers.get(t.id);
  if (existing) clearTimeout(existing);
  const handle = setTimeout(() => {
    offsetSyncTimers.delete(t.id);
    const tg = getWebApp();
    if (!tg) return;
    void updateTrackOffset({
      initData: tg.initData,
      sketchId: sketchId!,
      trackId: t.id,
      offsetMs: t.offsetMs,
    }).catch((e) => {
      console.warn('offset sync failed:', e);
    });
  }, 400);
  offsetSyncTimers.set(t.id, handle);
}

async function syncTrackGainToServer(t: Track): Promise<void> {
  if (!sketchId) return;
  if (!t.id || /^t_/.test(t.id)) return;
  const tg = getWebApp();
  if (!tg) return;
  try {
    await updateTrackGain({
      initData: tg.initData,
      sketchId: sketchId!,
      trackId: t.id,
      gain: t.gain,
    });
  } catch (e) {
    console.warn('gain sync failed:', e);
  }
}

async function syncTrackPanToServer(t: Track): Promise<void> {
  if (!sketchId) return;
  if (!t.id || /^t_/.test(t.id)) return;
  const tg = getWebApp();
  if (!tg) return;
  try {
    await updateTrackPan({
      initData: tg.initData,
      sketchId: sketchId!,
      trackId: t.id,
      pan: t.pan,
    });
  } catch (e) {
    console.warn('pan sync failed:', e);
  }
}

const startSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();
function syncTrackStartAtToServer(t: Track): void {
  if (!sketchId) return;
  if (!t.id || /^t_/.test(t.id)) return;
  const existing = startSyncTimers.get(t.id);
  if (existing) clearTimeout(existing);
  const handle = setTimeout(() => {
    startSyncTimers.delete(t.id);
    const tg = getWebApp();
    if (!tg) return;
    void updateTrackStartAt({
      initData: tg.initData,
      trackId: t.id,
      startAtMs: t.startAtMs,
    }).catch((e) => {
      console.warn('start-at sync failed:', e);
    });
  }, 400);
  startSyncTimers.set(t.id, handle);
}

async function startRecording(): Promise<void> {
  try {
    await engine.startRecording({
      onRecordStart: () => {
        // Transition from count-in to recording display
        setHint('tap to stop');
        refreshRecordButton();
      },
    });
  } catch (e) {
    notify('error');
    const msg = (e as Error).message || 'unable to access microphone';
    console.error('record failed:', e);
    showError(`record failed: ${msg}`);
    refreshRecordButton();
    return;
  }

  haptic('heavy');
  els.record.setAttribute('aria-label', 'Stop recording');
  els.send.disabled = true;
  refreshRecordButton();
  refreshPlayButton();

  if (engine.isCountingIn) {
    setHint('count-in — playing 4 beats…');
    setStatus('counting in');
  } else {
    setHint('tap to stop');
  }

  timer = setInterval(() => {
    if (engine.isCountingIn) {
      setStatus('counting in…');
    } else if (engine.isRecording) {
      setStatus(`recording — ${fmtMs(engine.recordingElapsedMs)}`);
    }
  }, 150);
}

async function stopRecording(): Promise<void> {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  // If we're still in the count-in phase, cancel cleanly with no take
  if (engine.isCountingIn) {
    engine.cancelCountIn();
    haptic('light');
    els.record.setAttribute('aria-label', 'Record');
    setStatus(engine.tracks.length === 0 ? 'no tracks yet' : `${engine.tracks.length} tracks`);
    setHint('tap to record');
    refreshRecordButton();
    refreshSendButton();
    refreshPlayButton();
    return;
  }

  setStatus('finishing capture…');
  setHint('decoding');
  els.record.disabled = true;

  let track: Track;
  try {
    track = await engine.stopRecording();
  } catch (e) {
    notify('error');
    const msg = (e as Error).message || 'capture failed';
    console.error('stop failed:', e);
    showError(`stop failed: ${msg}`);
    els.record.disabled = false;
    return;
  }

  haptic('medium');
  notify('success');
  els.record.disabled = false;
  els.record.setAttribute('aria-label', 'Record');
  refreshTrackList();
  refreshRecordButton();

  // Capture diagnostic is still available on engine.lastCaptureDiagnostic
  // for console inspection. Not surfaced as a banner anymore — at 16 kHz
  // exact iOS delivers consistently clean audio.
}

async function doSend(): Promise<void> {
  if (engine.isRecording) return;
  const audible = engine.tracks.filter((t) => !t.muted);
  if (audible.length === 0) return;

  const tg = getWebApp();
  if (!tg) {
    setStatus('not in Telegram — cannot upload');
    return;
  }

  // Upload EVERY local track that hasn't been sent yet (originalBlob !== null).
  // Imported tracks from a reopened sketch don't have originalBlob and are
  // already on the server, so they're skipped.
  const locals = engine.tracks.filter((t) => t.originalBlob !== null);
  if (locals.length === 0) {
    setStatus('nothing new to upload');
    setHint('record a layer first');
    return;
  }

  isUploading = true;
  refreshSendButton();
  refreshPlayButton();
  haptic('medium');

  for (let i = 0; i < locals.length; i++) {
    const t = locals[i];
    const blob = t.originalBlob!;
    setStatus(`uploading ${i + 1}/${locals.length} — ${(blob.size / 1024).toFixed(0)} KB`);
    setHint(locals.length === 1 ? 'sending stem' : `sending ${i + 1} of ${locals.length}`);

    let resp;
    try {
      resp = await uploadTrack({
        initData: tg.initData,
        sketchId,
        sketchName,
        stem: blob,
        durationMs: t.durationMs,
        offsetMs: t.offsetMs,
        startAtMs: t.startAtMs,
        tempoBpm: engine.metronome.getBpm(),
      });
    } catch (e) {
      notify('error');
      setStatus(`upload failed at ${i + 1}/${locals.length}`);
      setHint((e as Error).message);
      isUploading = false;
      refreshSendButton();
      refreshPlayButton();
      return;
    }

    // Lock sketch context after first upload so subsequent uploads append
    sketchId = resp.sketch_id;
    sketchName = resp.sketch_name;
    // Track is now server-owned — clear blob so next send doesn't re-upload
    t.originalBlob = null;
    if (resp.track_id) t.id = resp.track_id;
  }

  notify('success');
  const totalTracks = engine.tracks.length;
  setStatus(`sent ✓ — ${totalTracks} track${totalTracks === 1 ? '' : 's'} on the server`);
  setHint('add another layer or close');
  isUploading = false;
  refreshSendButton();
  refreshPlayButton();
}

async function loadExistingSketch(id: string): Promise<void> {
  isLoading = true;
  setStatus('loading sketch…');
  setHint('fetching tracks');
  refreshSendButton();

  const tg = getWebApp();
  const initData = tg?.initData ?? '';

  try {
    const data = await fetchSketch(id, initData);
    sketchId = data.sketch.id;
    sketchName = data.sketch.name;
    if (typeof data.sketch.tempo_bpm === 'number') {
      engine.metronome.setBpm(data.sketch.tempo_bpm);
      refreshTempo();
    }

    const settled = await Promise.allSettled(
      data.tracks.map(async (st) => {
        const buf = await fetchStem(st.audio_url, initData);
        return { st, buf };
      })
    );
    for (let i = 0; i < settled.length; i++) {
      const r = settled[i];
      const st = data.tracks[i];
      if (r.status === 'rejected') {
        console.warn('failed to fetch stem', st.id, r.reason);
        continue;
      }
      try {
        await engine.importTrack({
          arrayBuffer: r.value.buf,
          name: `track ${st.order_idx + 1}`,
          durationMs: st.duration_ms ?? undefined,
          offsetMs: st.offset_ms ?? 0,
          startAtMs: st.start_at_ms ?? 0,
          gain: typeof st.gain === 'number' ? st.gain : 1.0,
          pan: typeof st.pan === 'number' ? st.pan : 0.0,
          muted: !!st.muted,
          serverId: st.id,
        });
      } catch (e) {
        console.warn('failed to decode stem', st.id, e);
      }
    }
    setStatus(`${sketchName} — loaded ${engine.tracks.length} track${engine.tracks.length === 1 ? '' : 's'}`);
    setHint('tap record to add a layer');
  } catch (e) {
    setStatus('failed to load sketch');
    setHint((e as Error).message);
  } finally {
    isLoading = false;
    refreshTrackList();
  }
}

function readSketchIdFromUrl(): { id: string | null; source: string } {
  // 1. Explicit query param (when opening the Mini App URL directly)
  const params = new URLSearchParams(window.location.search);
  const direct = params.get('sketch');
  if (direct) return { id: direct, source: '?sketch=' };

  // 2. Telegram's initDataUnsafe.start_param — this is the canonical place
  // Direct Link Mini Apps put the value here.
  const tg = getWebApp();
  const startParam = tg?.initDataUnsafe.start_param;
  if (startParam && startParam.startsWith('s_')) return { id: startParam.slice(2), source: 'start_param' };

  // 3. Some older Telegram clients put it in the URL hash as
  //    tgWebAppStartParam=…
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const hashStart = hashParams.get('tgWebAppStartParam');
  if (hashStart && hashStart.startsWith('s_')) return { id: hashStart.slice(2), source: 'hash' };

  // Diagnostic: surface what we DID see so we can debug Edit-button flow
  const diag = `sp=${startParam ?? 'null'} hash=${window.location.hash.slice(1, 40) || 'null'}`;
  return { id: null, source: diag };
}

function readSketchNameFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('name');
}

async function init(): Promise<void> {
  const tg = bootstrapTelegram();
  if (!tg) {
    const banner = document.getElementById('load-error');
    if (banner) banner.hidden = false;
  }

  // Surface any otherwise-silent runtime errors (promise rejections, async
  // throws inside event handlers) in the red banner — iOS swallows the
  // console, so the user has no other way to see what failed.
  window.addEventListener('error', (ev) => {
    showError(`${ev.message || ev.error}`.slice(0, 200));
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    showError(`unhandled: ${msg}`.slice(0, 200));
  });

  // Tag the body for iOS-only CSS hooks (e.g. headphones hint during
  // recording — iOS PlayAndRecord routing makes speaker playback quiet
  // while the mic is live and there is no JS workaround).
  document.body.dataset.ios = isIOS() ? 'true' : 'false';

  els.record.addEventListener('click', () => {
    // Unlock Tone synchronously inside the click event — iOS requires
    // AudioContext.resume() to be invoked from a user gesture, and once
    // we cross an `await`, we may have already left gesture context.
    void Tone.start();
    if (engine.isRecording || engine.isCountingIn) {
      void stopRecording();
      return;
    }
    if (!engine.getArmedTrack()) {
      if (engine.tracks.length > 0) {
        engine.armTrack(engine.tracks[engine.tracks.length - 1].id);
      } else {
        engine.addEmptyArmedTrack();
      }
      refreshTrackList();
    }
    void startRecording();
  });

  els.addTrack.addEventListener('click', () => {
    void Tone.start();
    if (engine.isRecording || engine.isCountingIn) return;
    engine.setPlayheadMs(0);
    engine.addEmptyArmedTrack();
    haptic('medium');
    refreshTrackList();
  });
  els.send.addEventListener('click', () => {
    void doSend();
  });

  els.play.addEventListener('click', async () => {
    void Tone.start();
    if (engine.isPlaying) {
      engine.stopPlayback();
      haptic('light');
      return;
    }
    if (engine.isRecording) return;
    haptic('medium');
    try {
      // Await the async setup so that by the time refreshPlayButton runs
      // engine.playbackSources is populated and isPlaying === true.
      await engine.play(() => {
        // Fires when playback ends (naturally or via stopPlayback).
        refreshPlayButton();
      });
    } catch (e) {
      console.warn('playback failed:', e);
    }
    refreshPlayButton();
  });

  els.countInToggle.addEventListener('click', () => {
    setCountInEnabled(!engine.getCountInEnabled());
    haptic('light');
  });
  els.speakerBoostToggle.addEventListener('click', () => {
    setSpeakerBoostEnabled(engine.getOverdubBoost() <= 1);
    haptic('light');
  });
  els.metronomeRecToggle.addEventListener('click', () => {
    setMetronomeDuringRecording(!engine.getMetronomeDuringRecording());
    haptic('light');
  });
  els.settingsToggle.addEventListener('click', () => {
    setSettingsDrawerOpen(!isDrawerOpen());
    haptic('light');
  });
  els.settingsToggleIcon.appendChild(icon('sliders-horizontal', 20));
  els.addTrackIcon.appendChild(icon('plus', 22));
  els.tapTempo.addEventListener('click', () => {
    haptic('light');
    const bpm = tapTempo.tap();
    if (bpm) {
      engine.metronome.setBpm(bpm);
      refreshTempo();
    }
    els.tapTempo.classList.add('tapping');
    window.setTimeout(() => els.tapTempo.classList.remove('tapping'), 150);
  });

  els.offsetUp.addEventListener('click', () => {
    haptic('light');
    setLatencyOffset(engine.getLatencyOffsetMs() + LATENCY_STEP_MS);
  });
  els.offsetDown.addEventListener('click', () => {
    haptic('light');
    setLatencyOffset(engine.getLatencyOffsetMs() - LATENCY_STEP_MS);
  });

  setButtonIcon(els.rewindIcon, 'skip-back', 16);
  els.rewind.addEventListener('click', () => {
    haptic('light');
    engine.seek(0);
  });

  els.timelineArea.addEventListener('click', (ev) => {
    if (engine.transportState === 'recording') return;
    // Don't seek when clicking on a track lane's controls (mute, settings, etc.)
    const target = ev.target as HTMLElement;
    if (target.closest('.lane-btns') || target.closest('.lane-header') || target.closest('.track-settings-panel')) {
      return;
    }
    const rect = els.timelineArea.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const total = engine.sketchDurationMs;
    engine.seek(Math.round(ratio * total));
    haptic('light');
  });

  engine.onPlayhead((_ms, state) => {
    refreshPlayButton();
    refreshTransportTime();
    updatePlayheadPosition();
    if (state === 'playing' || state === 'recording') startPlayheadLoop();
    else stopPlayheadLoop();
    if (state === 'recording') {
      // The placeholder lane was just pushed; re-render the list so it
      // appears, then start the throttled repaint loop.
      refreshTrackList();
      startLiveRecordPaint();
    } else {
      stopLiveRecordPaint();
    }
  });

  const ro = new ResizeObserver(() => {
    updatePlayheadPosition();
    // Repaint all lane canvases at the new width.
    for (const li of els.tracksList.querySelectorAll('li.track-lane')) {
      const canvas = li.querySelector('canvas.lane-canvas') as HTMLCanvasElement | null;
      const trackId = (li as HTMLElement).dataset.trackId;
      if (!canvas || !trackId) continue;
      const t = engine.tracks.find((x) => x.id === trackId);
      if (!t) continue;
      paintLaneCanvas(canvas, t, els.timelineArea.clientWidth || 320);
    }
  });
  ro.observe(els.timelineArea);

  const teardown = () => engine.dispose();
  window.addEventListener('pagehide', teardown);
  window.addEventListener('beforeunload', teardown);

  loadStoredOffset();
  loadStoredCountIn();
  loadStoredSpeakerBoost();
  loadStoredMetronomeRec();
  loadStoredDrawerState();
  refreshOffset();
  refreshTempo();
  refreshSpeakerBoost();
  refreshMetronomeRec();
  refreshTrackList();

  const parsed = readSketchIdFromUrl();
  if (parsed.id) {
    await loadExistingSketch(parsed.id);
  } else {
    sketchName = readSketchNameFromUrl();
    // No more debug text in the footer — keep the UI minimal. Sketch-id
    // tracing is still available via the URL bar / startapp param.
  }

  // Boot invariant: always have one armed track. If we loaded an existing
  // sketch, arm its most recent track; otherwise create an empty armed one.
  ensureArmedAfterBoot(engine);
  refreshTrackList();
}

/**
 * Boot-time arming invariant. Exposed for unit tests.
 *
 * If no tracks exist (fresh sketch), creates one empty armed track so the
 * user can press record immediately. If tracks exist (reopened sketch) but
 * none are armed, arms the most recently-added.
 */
export function ensureArmedAfterBoot(e: AudioEngine): void {
  if (e.tracks.length === 0) {
    e.addEmptyArmedTrack();
  } else if (!e.getArmedTrack()) {
    e.armTrack(e.tracks[e.tracks.length - 1].id);
  }
}

void init();
